import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Search, MapPin, Heart, DollarSign, Users, Calendar, Award, ChevronDown, X, Filter, Check, Plane } from 'lucide-react';
import schoolsData from '../data/schools.json';
import heroImage from '../assets/Program/CPLHU.JPG';

// --- Styled Components ---

const PageWrapper = styled.div`
  font-family: 'Inter', sans-serif;
  background: #f8f9fa;
  min-height: 100vh;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 140px 20px 100px;
  background: 
    linear-gradient(135deg, rgba(0, 31, 63, 0.95) 0%, rgba(0, 86, 179, 0.92) 50%, rgba(0, 61, 130, 0.94) 100%),
    url(${heroImage}) center/cover no-repeat fixed;
  color: white;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    padding: 120px 20px 80px;
  }

  @media (max-width: 480px) {
    padding: 100px 15px 60px;
  }

  h1 {
    font-size: clamp(2.2rem, 6vw, 4.5rem);
    font-weight: 900;
    line-height: 1.1;
    text-shadow: 0 4px 30px rgba(0,0,0,0.5);
    margin-bottom: 20px;
    
    span {
      background: linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      display: inline-block;
    }
  }

  p {
    font-size: clamp(1rem, 2.5vw, 1.3rem);
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.95;
    line-height: 1.7;
    text-shadow: 0 2px 10px rgba(0,0,0,0.3);
  }
`;

const MainContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 20px 80px;
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 280px 1fr;
    gap: 30px;
    padding: 30px 15px 60px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 20px 15px 50px;
  }
`;

const FilterSidebar = styled.aside`
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 100px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #0056b3;
    border-radius: 10px;
  }

  @media (max-width: 1200px) {
    padding: 20px;
  }

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    border-radius: 0;
    max-height: 100vh;
    padding: 20px;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
  }
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e9ecef;

  h3 {
    font-size: 1.4rem;
    font-weight: 800;
    color: #001f3f;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: #6c757d;
    transition: all 0.3s ease;

    &:hover {
      color: #0056b3;
      transform: scale(1.1);
    }
  }
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 25px;

  input {
    width: 100%;
    padding: 14px 45px 14px 18px;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background: #f8f9fa;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #0056b3;
      background: white;
      box-shadow: 0 4px 12px rgba(0, 86, 179, 0.1);
    }

    &::placeholder {
      color: #adb5bd;
    }
  }

  svg {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    pointer-events: none;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const FilterTitle = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  padding: 12px 0;
  font-size: 1rem;
  font-weight: 700;
  color: #212529;
  cursor: pointer;
  transition: color 0.3s ease;

  span {
    white-space: nowrap;
  }

  &:hover {
    color: #0056b3;
  }

  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const FilterOptions = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding-top: 15px;
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-size: 0.93rem;
  color: #495057;

  &:hover {
    background: #f8f9fa;
    color: #0056b3;
  }

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #0056b3;
  }

  .count {
    margin-left: auto;
    font-size: 0.85rem;
    color: #6c757d;
    font-weight: 600;
    background: #e9ecef;
    padding: 2px 8px;
    border-radius: 10px;
  }
`;

const PriceRangeSlider = styled.div`
  padding: 20px 10px 10px;

  .price-labels {
    display: flex;
    justify-content: space-between;
    margin-bottom: 25px;
    font-size: 0.9rem;
    color: #495057;
    font-weight: 600;

    span {
      padding: 6px 12px;
      background: linear-gradient(135deg, #f0f7ff, #e0f0ff);
      border-radius: 10px;
      border: 1px solid rgba(0, 86, 179, 0.15);
      color: #0056b3;
      font-weight: 700;
    }
  }

  .slider-container {
    position: relative;
    height: 6px;
    background: linear-gradient(to right, #e9ecef, #cbd5e0);
    border-radius: 10px;
    margin: 20px 0 30px;
  }

  .slider-track {
    position: absolute;
    height: 6px;
    background: linear-gradient(135deg, #0056b3, #003d82);
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 86, 179, 0.3);
  }

  input[type="range"] {
    position: absolute;
    width: 100%;
    height: 6px;
    background: none;
    pointer-events: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
    top: 0;
    left: 0;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #0056b3, #003d82);
      border: 3px solid white;
      cursor: pointer;
      pointer-events: auto;
      box-shadow: 0 2px 8px rgba(0, 86, 179, 0.4);
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.2);
        box-shadow: 0 4px 12px rgba(0, 86, 179, 0.5);
      }

      &:active {
        transform: scale(1.1);
      }
    }

    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #0056b3, #003d82);
      border: 3px solid white;
      cursor: pointer;
      pointer-events: auto;
      box-shadow: 0 2px 8px rgba(0, 86, 179, 0.4);
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.2);
        box-shadow: 0 4px 12px rgba(0, 86, 179, 0.5);
      }

      &:active {
        transform: scale(1.1);
      }
    }
  }
`;

const ClearButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #6c757d, #495057);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0056b3, #003d82);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 86, 179, 0.3);
  }
`;

const ResultsSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 35px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  h2 {
    font-size: 1.8rem;
    font-weight: 800;
    color: #001f3f;
  }

  .count {
    color: #6c757d;
    font-size: 1.05rem;
  }
`;

const MobileFilterButton = styled.button`
  display: none;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0056b3, #003d82);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  gap: 8px;
  align-items: center;
  box-shadow: 0 4px 15px rgba(0, 86, 179, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 86, 179, 0.4);
  }

  @media (max-width: 1024px) {
    display: flex;
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 25px;
`;

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 15px;
  background: linear-gradient(135deg, #e6f2ff, #cce5ff);
  color: #0056b3;
  border-radius: 20px;
  font-size: 0.88rem;
  font-weight: 600;
  border: 1px solid rgba(0, 86, 179, 0.2);

  button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: #0056b3;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.2);
      color: #003d82;
    }
  }
`;

const SchoolsGrid = styled.div`
  display: grid;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 25px;
  }
`;

const SchoolCard = styled(Link)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 20px;
  text-decoration: none;
  color: inherit;
  display: grid;
  grid-template-columns: minmax(240px, 280px) 1fr minmax(auto, 200px);
  gap: 25px;
  padding: 25px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 86, 179, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #0056b3, #FFD700, #FFA500, #0056b3);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(0, 31, 63, 0.15);
    border-color: rgba(255, 165, 0, 0.3);

    &::before {
      transform: scaleX(1);
    }

    .thumbnail img {
      transform: scale(1.1);
    }

    .save-btn {
      opacity: 1;
    }

    .view-btn {
      background: linear-gradient(135deg, #003d82, #001f3f);
      transform: translateX(5px);
    }
  }

  @media (max-width: 1200px) {
    grid-template-columns: 220px 1fr;
    gap: 20px;
    padding: 20px;

    .actions {
      grid-column: 1 / -1;
      flex-direction: row;
      justify-content: space-between;
      border-top: 1px solid #e9ecef;
      padding-top: 20px;
      margin-top: 15px;
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 20px;
  }

  @media (max-width: 640px) {
    padding: 15px;
    gap: 15px;
  }

  .thumbnail {
    position: relative;
    border-radius: 15px;
    overflow: hidden;
    height: 220px;
    background: #e9ecef;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
    }

    .badge {
      position: absolute;
      top: 12px;
      left: 12px;
      background: linear-gradient(135deg, #FFD700, #FFA500);
      color: #001f3f;
      padding: 6px 14px;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 800;
      box-shadow: 0 4px 15px rgba(255, 165, 0, 0.4);
    }

    .save-btn {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.95);
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

      &:hover {
        background: #ff4757;
        color: white;
        transform: scale(1.1);
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 10px 0;
  }

  .school-header {
    h3 {
      font-size: 1.6rem;
      font-weight: 800;
      color: #001f3f;
      margin-bottom: 8px;
      line-height: 1.3;
    }

    .location {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #6c757d;
      font-size: 0.95rem;
      font-weight: 600;

      .flag {
        font-size: 1.2rem;
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  .description {
    color: #495057;
    font-size: 0.95rem;
    line-height: 1.7;
    flex-grow: 1;
  }

  .meta-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .tag {
    background: linear-gradient(135deg, #f0f7ff, #e0f0ff);
    color: #1e3a8a;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 700;
    border: 1.5px solid rgba(0, 86, 179, 0.15);
  }

  .stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding-top: 15px;
    border-top: 1px solid #e9ecef;

    @media (max-width: 640px) {
      gap: 8px;
    }
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .label {
      font-size: 0.8rem;
      color: #6c757d;
      font-weight: 600;
    }

    .value {
      font-size: 1.1rem;
      font-weight: 800;
      color: #0056b3;
    }
  }

  .actions {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    padding: 10px 0;
    gap: 20px;

    @media (max-width: 1024px) {
      flex-direction: row;
      align-items: center;
    }
  }

  .price {
    text-align: right;

    .from {
      font-size: 0.85rem;
      color: #6c757d;
      font-weight: 600;
    }

    .amount {
      font-size: 2rem;
      font-weight: 900;
      color: #0056b3;
      line-height: 1;
      margin-top: 5px;
    }

    .currency {
      font-size: 1.2rem;
    }
  }

  .view-btn {
    padding: 14px 32px;
    background: linear-gradient(135deg, #0056b3, #003d82);
    color: white;
    border: none;
    border-radius: 10px;
    font-weight: 700;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.4s ease;
    white-space: nowrap;
    box-shadow: 0 4px 15px rgba(0, 86, 179, 0.3);

    &:hover {
      box-shadow: 0 6px 20px rgba(0, 86, 179, 0.5);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6c757d;

  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    opacity: 0.3;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #495057;
    margin-bottom: 10px;
  }

  p {
    font-size: 1.05rem;
  }
`;

const SchoolListingTemplate = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLicenses, setSelectedLicenses] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState({
    destination: true,
    category: true,
    license: true,
    price: true,
    amenities: true
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleFilter = (filterArray, setFilter, value) => {
    if (filterArray.includes(value)) {
      setFilter(filterArray.filter(item => item !== value));
    } else {
      setFilter([...filterArray, value]);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedDestinations([]);
    setSelectedCategories([]);
    setSelectedLicenses([]);
    setSelectedAmenities([]);
    setPriceRange([0, 100000]);
  };

  // Filter schools based on all criteria
  const filteredSchools = useMemo(() => {
    return schoolsData.schools.filter(school => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          school.name.toLowerCase().includes(search) ||
          school.location.city.toLowerCase().includes(search) ||
          school.location.country.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Destination filter
      if (selectedDestinations.length > 0) {
        const countryMatch = selectedDestinations.some(dest => 
          school.location.country.toLowerCase().replace(/\s+/g, '-') === dest
        );
        if (!countryMatch) return false;
      }

      // Category filter
      if (selectedCategories.length > 0) {
        if (!selectedCategories.includes(school.category)) return false;
      }

      // License filter
      if (selectedLicenses.length > 0) {
        const hasLicense = school.license_authority.some(license => 
          selectedLicenses.includes(license)
        );
        if (!hasLicense) return false;
      }

      // Amenities filter
      if (selectedAmenities.length > 0) {
        const hasAmenity = selectedAmenities.every(amenity => 
          school.amenities.includes(amenity)
        );
        if (!hasAmenity) return false;
      }

      // Price filter
      const price = school.starting_price.amount;
      if (price < priceRange[0] || price > priceRange[1]) return false;

      return true;
    });
  }, [searchTerm, selectedDestinations, selectedCategories, selectedLicenses, selectedAmenities, priceRange]);

  const activeFiltersCount = 
    selectedDestinations.length + 
    selectedCategories.length + 
    selectedLicenses.length + 
    selectedAmenities.length +
    (searchTerm ? 1 : 0);

  return (
    <PageWrapper>
      <HeroSection>
        <h1>{schoolsData.hero.title} <span>{schoolsData.hero.titleHighlight}</span></h1>
        <p>{schoolsData.hero.description}</p>
      </HeroSection>

      <MainContent>
        <FilterSidebar isOpen={isSidebarOpen}>
          <FilterHeader>
            <h3>
              <Filter size={20} />
              Filters
            </h3>
            <button onClick={() => setIsSidebarOpen(false)}>
              <X size={24} />
            </button>
          </FilterHeader>

          <SearchBox>
            <input
              type="text"
              placeholder="Search by school name, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search size={18} />
          </SearchBox>

          <FilterSection>
            <FilterTitle onClick={() => toggleSection('destination')} isOpen={openSections.destination}>
              <span>Destination</span>
              <ChevronDown size={18} />
            </FilterTitle>
            <FilterOptions isOpen={openSections.destination}>
              {schoolsData.filters.destinations.map(dest => (
                <CheckboxLabel key={dest.id}>
                  <input
                    type="checkbox"
                    checked={selectedDestinations.includes(dest.id)}
                    onChange={() => toggleFilter(selectedDestinations, setSelectedDestinations, dest.id)}
                  />
                  <span>{dest.label}</span>
                  <span className="count">{dest.count}</span>
                </CheckboxLabel>
              ))}
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterTitle onClick={() => toggleSection('category')} isOpen={openSections.category}>
              <span>Course Category</span>
              <ChevronDown size={18} />
            </FilterTitle>
            <FilterOptions isOpen={openSections.category}>
              {schoolsData.filters.categories.map(cat => (
                <CheckboxLabel key={cat.id}>
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat.id)}
                    onChange={() => toggleFilter(selectedCategories, setSelectedCategories, cat.id)}
                  />
                  <span>{cat.label}</span>
                </CheckboxLabel>
              ))}
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterTitle onClick={() => toggleSection('license')} isOpen={openSections.license}>
              <span>License Authority</span>
              <ChevronDown size={18} />
            </FilterTitle>
            <FilterOptions isOpen={openSections.license}>
              {schoolsData.filters.licenses.map(license => (
                <CheckboxLabel key={license.id}>
                  <input
                    type="checkbox"
                    checked={selectedLicenses.includes(license.id)}
                    onChange={() => toggleFilter(selectedLicenses, setSelectedLicenses, license.id)}
                  />
                  <span>{license.label}</span>
                </CheckboxLabel>
              ))}
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterTitle onClick={() => toggleSection('price')} isOpen={openSections.price}>
              <span>Budget Range</span>
              <ChevronDown size={18} />
            </FilterTitle>
            <FilterOptions isOpen={openSections.price}>
              <PriceRangeSlider>
                <div className="price-labels">
                  <span>${priceRange[0].toLocaleString()}</span>
                  <span>${priceRange[1].toLocaleString()}</span>
                </div>
                <div className="slider-container">
                  <div
                    className="slider-track"
                    style={{
                      left: `${(priceRange[0] / 100000) * 100}%`,
                      right: `${100 - (priceRange[1] / 100000) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="5000"
                    value={priceRange[0]}
                    onChange={(e) => {
                      const value = Math.min(Number(e.target.value), priceRange[1] - 5000);
                      setPriceRange([value, priceRange[1]]);
                    }}
                    style={{ zIndex: priceRange[0] > priceRange[1] - 10000 ? 5 : 3 }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="5000"
                    value={priceRange[1]}
                    onChange={(e) => {
                      const value = Math.max(Number(e.target.value), priceRange[0] + 5000);
                      setPriceRange([priceRange[0], value]);
                    }}
                    style={{ zIndex: 4 }}
                  />
                </div>
              </PriceRangeSlider>
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterTitle onClick={() => toggleSection('amenities')} isOpen={openSections.amenities}>
              <span>Amenities</span>
              <ChevronDown size={18} />
            </FilterTitle>
            <FilterOptions isOpen={openSections.amenities}>
              {schoolsData.filters.amenities.map(amenity => (
                <CheckboxLabel key={amenity.id}>
                  <input
                    type="checkbox"
                    checked={selectedAmenities.includes(amenity.id)}
                    onChange={() => toggleFilter(selectedAmenities, setSelectedAmenities, amenity.id)}
                  />
                  <span>{amenity.label}</span>
                </CheckboxLabel>
              ))}
            </FilterOptions>
          </FilterSection>

          {activeFiltersCount > 0 && (
            <ClearButton onClick={clearAllFilters}>
              Clear All Filters ({activeFiltersCount})
            </ClearButton>
          )}
        </FilterSidebar>

        <ResultsSection>
          <ResultsHeader>
            <div>
              <h2>Flight Schools</h2>
              <p className="count">{filteredSchools.length} schools found</p>
            </div>
            <MobileFilterButton onClick={() => setIsSidebarOpen(true)}>
              <Filter size={18} />
              Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </MobileFilterButton>
          </ResultsHeader>

          {activeFiltersCount > 0 && (
            <ActiveFilters>
              {searchTerm && (
                <FilterChip>
                  Search: "{searchTerm}"
                  <button onClick={() => setSearchTerm('')}>
                    <X size={14} />
                  </button>
                </FilterChip>
              )}
              {selectedDestinations.map(dest => (
                <FilterChip key={dest}>
                  {schoolsData.filters.destinations.find(d => d.id === dest)?.label}
                  <button onClick={() => toggleFilter(selectedDestinations, setSelectedDestinations, dest)}>
                    <X size={14} />
                  </button>
                </FilterChip>
              ))}
              {selectedLicenses.map(license => (
                <FilterChip key={license}>
                  {license}
                  <button onClick={() => toggleFilter(selectedLicenses, setSelectedLicenses, license)}>
                    <X size={14} />
                  </button>
                </FilterChip>
              ))}
            </ActiveFilters>
          )}

          {filteredSchools.length > 0 ? (
            <SchoolsGrid>
              {filteredSchools.map(school => (
                <SchoolCard key={school.id} to={`/schools/${school.slug}`}>
                  <div className="thumbnail">
                    <img src={school.hero_image} alt={school.name} />
                    {school.badges[0] && <div className="badge">{school.badges[0]}</div>}
                    <button className="save-btn" onClick={(e) => e.preventDefault()}>
                      <Heart />
                    </button>
                  </div>

                  <div className="content">
                    <div className="school-header">
                      <h3>{school.name}</h3>
                      <div className="location">
                        <span className="flag">{school.location.flag}</span>
                        <MapPin />
                        {school.location.city}, {school.location.country}
                      </div>
                    </div>

                    <p className="description">{school.overview_text.substring(0, 150)}...</p>

                    <div className="meta-tags">
                      {school.license_authority.slice(0, 2).map(auth => (
                        <span key={auth} className="tag">{auth} Approved</span>
                      ))}
                      {school.badges.slice(1, 3).map(badge => (
                        <span key={badge} className="tag">{badge}</span>
                      ))}
                    </div>

                    <div className="stats">
                      <div className="stat-item">
                        <span className="label">Founded</span>
                        <span className="value">{school.stats.founded}</span>
                      </div>
                      <div className="stat-item">
                        <span className="label">Fleet Size</span>
                        <span className="value">{school.stats.fleet_size}</span>
                      </div>
                      <div className="stat-item">
                        <span className="label">Graduates</span>
                        <span className="value">{school.stats.graduates}</span>
                      </div>
                    </div>
                  </div>

                  <div className="actions">
                    <div className="price">
                      <div className="from">From</div>
                      <div className="amount">
                        <span className="currency">{school.starting_price.currency === 'USD' ? '$' : school.starting_price.currency}</span>
                        {school.starting_price.amount.toLocaleString()}
                      </div>
                    </div>
                    <button className="view-btn">View Details</button>
                  </div>
                </SchoolCard>
              ))}
            </SchoolsGrid>
          ) : (
            <EmptyState>
              <Plane />
              <h3>No schools found</h3>
              <p>Try adjusting your filters or search criteria</p>
            </EmptyState>
          )}
        </ResultsSection>
      </MainContent>
    </PageWrapper>
  );
};

export default SchoolListingTemplate;
